import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import { User, Patient, Encounter, LoginSession, AuditEvent, MaternityFile, WalkInClient, BillingItem, Invoice, Quotation, Payment, CreditNote, BillingAuditEvent, Medicine, MedicineBatch, Supplier, PurchaseOrder, StockMovement, StockMovementInput, StockReceiptInput, MedicineInput } from '../types';
import { db } from './firebase';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, writeBatch, query } from 'firebase/firestore';
interface DataContextType {
  currentUser: User | null;
  users: User[];
  patients: Patient[];
  encounters: Encounter[];
  loginSessions: LoginSession[];
  auditEvents: AuditEvent[];
  maternityFiles: MaternityFile[];
  walkInClients: WalkInClient[];
  billingItems: BillingItem[];
  invoices: Invoice[];
  quotations: Quotation[];
  payments: Payment[];
  creditNotes: CreditNote[];
  billingAuditEvents: BillingAuditEvent[];
  medicines: Medicine[];
  medicineBatches: MedicineBatch[];
  suppliers: Supplier[];
  purchaseOrders: PurchaseOrder[];
  stockMovements: StockMovement[];
  addMedicine: (medicine: MedicineInput) => void;
  updateMedicine: (id: string, medicine: Partial<MedicineInput>) => void;
  addSupplier: (supplier: Omit<Supplier, 'id' | 'createdAt'>) => void;
  updateSupplier: (id: string, supplier: Partial<Supplier>) => void;
  createPurchaseOrder: (purchaseOrder: Omit<PurchaseOrder, 'id' | 'orderNumber' | 'createdAt'>) => void;
  updatePurchaseOrder: (id: string, purchaseOrder: Partial<PurchaseOrder>) => void;
  receiveStock: (receipt: StockReceiptInput) => {
    ok: boolean;
    error?: string;
  };
  recordStockMovement: (movement: StockMovementInput) => {
    ok: boolean;
    error?: string;
  };
  dispenseMedicine: (input: {
    drugName: string;
    encounterId: string;
    patientId: string;
    prescriptionId: string;
  }) => {
    ok: boolean;
    error?: string;
  };
  login: (fullName: string, password: string) => {
    ok: boolean;
    error?: string;
  };
  logout: () => void;
  addUser: (user: Omit<User, 'id'>) => void;
  updateUser: (id: string, user: Partial<User>) => void;
  deleteUser: (id: string) => void;
  addPatient: (patient: Omit<Patient, 'id'>) => void;
  updatePatient: (id: string, patient: Partial<Patient>) => void;
  deletePatient: (id: string) => void;
  createEncounter: (patientId: string) => Encounter;
  updateEncounter: (id: string, encounter: Partial<Encounter>) => void;
  getPatientEncounters: (patientId: string) => Encounter[];
  getActiveEncounters: () => Encounter[];
  deleteLoginSessions: (sessionIds?: string[]) => void;
  deleteLoginSessionsBefore: (date: string) => void;
  deleteLoginSessionsInRange: (start: string, end: string) => void;
  logAudit: (event: Omit<AuditEvent, 'id' | 'at'>) => void;
  addMaternityFile: (file: Omit<MaternityFile, 'id'>) => void;
  updateMaternityFile: (id: string, file: Partial<MaternityFile>) => void;
  deleteMaternityFile: (id: string) => void;
  addWalkInClient: (client: Omit<WalkInClient, 'id'>) => WalkInClient;
  updateWalkInClient: (id: string, client: Partial<WalkInClient>) => void;
  addBillingItem: (item: Omit<BillingItem, 'id'>) => void;
  updateBillingItem: (id: string, item: Partial<BillingItem>) => void;
  deleteBillingItem: (id: string) => void;
  addInvoice: (invoice: Omit<Invoice, 'id'>) => void;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => void;
  addQuotation: (quotation: Omit<Quotation, 'id'>) => void;
  updateQuotation: (id: string, quotation: Partial<Quotation>) => void;
  addPayment: (payment: Omit<Payment, 'id'>) => void;
  addCreditNote: (creditNote: Omit<CreditNote, 'id'>) => void;
  updateCreditNote: (id: string, creditNote: Partial<CreditNote>) => void;
  logBillingAudit: (action: string, description: string) => void;
}
const DataContext = createContext<DataContextType | undefined>(undefined);
const STORAGE_KEY = 'hms_data_v1';
interface StorageData {
  users: User[];
  patients: Patient[];
  encounters: Encounter[];
}
const defaultAdmin: User = {
  id: 'admin-1',
  fullName: 'System Admin',
  password: 'AdminPassword123!',
  role: 'Administrator'
};
// Firestore collection names
const USERS_COL = 'users';
const PATIENTS_COL = 'patients';
const ENCOUNTERS_COL = 'encounters';
const SESSIONS_COL = 'loginSessions';
const AUDIT_COL = 'auditEvents';
const MATERNITY_COL = 'maternityFiles';
const WALKIN_COL = 'walkInClients';
const BILLING_ITEMS_COL = 'billingItems';
const INVOICES_COL = 'invoices';
const QUOTATIONS_COL = 'quotations';
const PAYMENTS_COL = 'payments';
const CREDIT_NOTES_COL = 'creditNotes';
const BILLING_AUDIT_COL = 'billingAuditEvents';
const MEDICINES_COL = 'medicines';
const MEDICINE_BATCHES_COL = 'medicineBatches';
const SUPPLIERS_COL = 'suppliers';
const PURCHASE_ORDERS_COL = 'purchaseOrders';
const STOCK_MOVEMENTS_COL = 'stockMovements';
const SESSION_ID_KEY = 'hms_session_id';
type MedicineDocument = Partial<Medicine> & {
  id: string;
};
const MEDICINE_MASTER_FIELDS: Array<keyof Medicine> = ['drugCode', 'medicineId', 'genericName', 'brandName', 'strength', 'dosageForm', 'unit', 'packSize', 'category', 'manufacturer', 'buyingPrice', 'sellingPrice', 'currentQuantity', 'reorderLevel', 'maximumStock', 'status', 'createdAt', 'updatedAt', 'name', 'sku', 'form', 'costPrice'];
function toText(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}
function toNumber(value: unknown, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
function medicineCodeSequence(value: unknown) {
  if (typeof value !== 'string') return 0;
  const match = value.trim().match(/^(?:DRG|MED)-(\d+)$/i);
  return match ? Number(match[1]) : 0;
}
function formatDrugCode(sequence: number) {
  return `DRG-${String(sequence).padStart(6, '0')}`;
}
function buildMedicineIdentifier(createdAt: string, documentId: string) {
  const timestamp = new Date(createdAt).getTime();
  const stableTimestamp = Number.isFinite(timestamp) ? timestamp : Date.now();
  const suffix = documentId.replace(/[^a-z0-9]/gi, '').slice(-6) || 'MASTER';
  return `MED-${stableTimestamp}-${suffix.toUpperCase()}`;
}
function normalizeMedicineRecord(source: MedicineDocument, drugCode: string, fallbackNow: string): Medicine {
  const createdAt = toText(source.createdAt, fallbackNow);
  const genericName = toText(source.genericName, toText(source.name, 'Unnamed medicine'));
  const brandName = toText(source.brandName, toText(source.name, genericName));
  const dosageForm = toText(source.dosageForm, toText(source.form, 'Not specified'));
  const reorderLevel = Math.max(0, toNumber(source.reorderLevel, 0));
  const currentQuantity = Math.max(0, toNumber(source.currentQuantity, 0));
  const buyingPrice = Math.max(0, toNumber(source.buyingPrice, toNumber(source.costPrice, 0)));
  return {
    id: source.id,
    drugCode,
    medicineId: toText(source.medicineId, buildMedicineIdentifier(createdAt, source.id)),
    genericName,
    brandName,
    strength: toText(source.strength, 'Not specified'),
    dosageForm,
    unit: toText(source.unit, dosageForm),
    packSize: Math.max(1, toNumber(source.packSize, 1)),
    category: toText(source.category, 'General'),
    manufacturer: toText(source.manufacturer, 'Not specified'),
    buyingPrice,
    sellingPrice: Math.max(0, toNumber(source.sellingPrice, 0)),
    currentQuantity,
    reorderLevel,
    maximumStock: Math.max(currentQuantity, reorderLevel, toNumber(source.maximumStock, Math.max(reorderLevel * 2, 0))),
    status: source.status === 'Inactive' ? 'Inactive' : 'Active',
    createdAt,
    updatedAt: toText(source.updatedAt, createdAt),
    // Existing pharmacy flows continue to consume these compatibility aliases.
    name: toText(source.name, brandName || genericName),
    sku: toText(source.sku, drugCode),
    form: dosageForm,
    costPrice: buyingPrice
  };
}
function normalizeMedicineRecords(records: MedicineDocument[]) {
  const highestKnownSequence = records.reduce((highest, record) => Math.max(highest, medicineCodeSequence(record.drugCode), medicineCodeSequence(record.sku)), 0);
  let nextSequence = highestKnownSequence + 1;
  const usedCodes = new Set<string>();
  const now = new Date().toISOString();
  return records.map(record => {
    const existingSequence = medicineCodeSequence(record.drugCode);
    const existingCode = existingSequence ? formatDrugCode(existingSequence) : '';
    const legacySequence = medicineCodeSequence(record.sku);
    let code: string;
    if (existingSequence && !usedCodes.has(existingCode)) {
      code = existingCode;
    } else if (legacySequence) {
      const legacyCode = formatDrugCode(legacySequence);
      if (!usedCodes.has(legacyCode)) code = legacyCode;else {
        while (usedCodes.has(formatDrugCode(nextSequence))) nextSequence += 1;
        code = formatDrugCode(nextSequence);
        nextSequence += 1;
      }
    } else {
      while (usedCodes.has(formatDrugCode(nextSequence))) nextSequence += 1;
      code = formatDrugCode(nextSequence);
      nextSequence += 1;
    }
    usedCodes.add(code);
    return normalizeMedicineRecord(record, code, now);
  });
}
function medicineBackfill(source: MedicineDocument, normalized: Medicine): Record<string, string | number> {
  return MEDICINE_MASTER_FIELDS.reduce<Record<string, string | number>>((patch, field) => {
    if (source[field] !== normalized[field]) patch[field] = normalized[field];
    return patch;
  }, {});
}
function buildNextDrugCode(existingMedicines: Medicine[]) {
  const highestSequence = existingMedicines.reduce((highest, medicine) => Math.max(highest, medicineCodeSequence(medicine.drugCode), medicineCodeSequence(medicine.sku)), 0);
  const usedCodes = new Set(existingMedicines.map(medicine => medicine.drugCode.toUpperCase()));
  let sequence = highestSequence + 1;
  while (usedCodes.has(formatDrugCode(sequence))) sequence += 1;
  return formatDrugCode(sequence);
}
// Parse navigator.userAgent into a short, readable device label
const parseDevice = (): {
  device: string;
  userAgent: string;
} => {
  if (typeof navigator === 'undefined') {
    return {
      device: 'Unknown device',
      userAgent: ''
    };
  }
  const ua = navigator.userAgent || '';
  // OS detection
  let os = 'Unknown OS';
  if (/Windows NT 10/i.test(ua)) os = 'Windows 10/11';else if (/Windows NT/i.test(ua)) os = 'Windows';else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';else if (/Android/i.test(ua)) os = 'Android';else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';else if (/Linux/i.test(ua)) os = 'Linux';else if (/CrOS/i.test(ua)) os = 'ChromeOS';
  // Browser detection (order matters — Edge before Chrome, etc.)
  let browser = 'Unknown browser';
  if (/Edg\//i.test(ua)) browser = 'Edge';else if (/OPR\/|Opera/i.test(ua)) browser = 'Opera';else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';else if (/Firefox\//i.test(ua)) browser = 'Firefox';else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
  // Device type
  const isMobile = /Mobi|Android.*Mobile|iPhone|iPod/i.test(ua);
  const isTablet = /iPad|Tablet|Android(?!.*Mobile)/i.test(ua);
  const deviceType = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop';
  return {
    device: `${browser} on ${os} · ${deviceType}`,
    userAgent: ua
  };
};
// Resolve approximate location from IP using a free public service.
// Best-effort: if the call fails, the session simply has no location.
const fetchLocation = async (): Promise<{
  location?: string;
  ipAddress?: string;
}> => {
  try {
    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) return {};
    const data = await res.json();
    const parts = [data.city, data.region, data.country_name].filter(Boolean);
    return {
      location: parts.length > 0 ? parts.join(', ') : undefined,
      ipAddress: data.ip
    };
  } catch (err) {
    console.warn('Location lookup failed:', err);
    return {};
  }
};
export const DataProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loginSessions, setLoginSessions] = useState<LoginSession[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [maternityFiles, setMaternityFiles] = useState<MaternityFile[]>([]);
  const [walkInClients, setWalkInClients] = useState<WalkInClient[]>([]);
  const [billingItems, setBillingItems] = useState<BillingItem[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [billingAuditEvents, setBillingAuditEvents] = useState<BillingAuditEvent[]>([]);
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [medicineBatches, setMedicineBatches] = useState<MedicineBatch[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  // Track whether Firestore has delivered initial data
  const firestoreReady = useRef({
    users: false,
    patients: false,
    encounters: false
  });
  // Holds prescription IDs while a dispense write is being committed, preventing
  // rapid repeat actions from producing duplicate deductions before snapshots refresh.
  const dispensingPrescriptionIdsRef = useRef(new Set<string>());
  // Keeps centrally generated codes unique during rapid consecutive medicine creation.
  const reservedDrugCodesRef = useRef(new Set<string>());
  // One-shot guard so the duplicate-BMC migration runs at most once per session
  const dedupeRanRef = useRef(false);
  // Load from localStorage as initial fallback, then Firestore takes over
  useEffect(() => {
    // Load localStorage cache first for instant display
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed: StorageData = JSON.parse(stored);
        setUsers(parsed.users?.length ? parsed.users : [defaultAdmin]);
        setPatients(parsed.patients || []);
        setEncounters(parsed.encounters || []);
      } catch (e) {
        console.error('Failed to parse stored data', e);
        setUsers([defaultAdmin]);
      }
    } else {
      setUsers([defaultAdmin]);
    }
    // Check session
    const session = sessionStorage.getItem('hms_session');
    if (session) {
      try {
        const user = JSON.parse(session);
        setCurrentUser(user);
      } catch (e) {
        // ignore
      }
    }
    setIsLoaded(true);
    // Set up Firestore real-time listeners
    const unsubUsers = onSnapshot(query(collection(db, USERS_COL)), snapshot => {
      if (!snapshot.empty) {
        const fireUsers = snapshot.docs.map(d => ({
          ...d.data(),
          id: d.id
        }) as User);
        setUsers(fireUsers);
      } else if (!firestoreReady.current.users) {
        // First load and empty — seed default admin to Firestore
        setDoc(doc(db, USERS_COL, defaultAdmin.id), defaultAdmin).catch(console.error);
        setUsers([defaultAdmin]);
      }
      firestoreReady.current.users = true;
    }, error => {
      console.error('Firestore users listener error:', error);
    });
    const unsubPatients = onSnapshot(query(collection(db, PATIENTS_COL)), snapshot => {
      const firePatients = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }) as Patient);
      setPatients(firePatients);
      firestoreReady.current.patients = true;
    }, error => {
      console.error('Firestore patients listener error:', error);
    });
    const unsubEncounters = onSnapshot(query(collection(db, ENCOUNTERS_COL)), snapshot => {
      const fireEncounters = snapshot.docs.map(d => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          labRequests: data.labRequests || [],
          radiologyRequests: data.radiologyRequests || [],
          prescriptions: data.prescriptions || []
        } as Encounter;
      });
      setEncounters(fireEncounters);
      firestoreReady.current.encounters = true;
    }, error => {
      console.error('Firestore encounters listener error:', error);
    });
    const unsubSessions = onSnapshot(query(collection(db, SESSIONS_COL)), snapshot => {
      const fireSessions = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }) as LoginSession);
      setLoginSessions(fireSessions);
    }, error => {
      console.error('Firestore loginSessions listener error:', error);
    });
    const unsubAudit = onSnapshot(collection(db, AUDIT_COL), snapshot => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AuditEvent[];
      setAuditEvents(data);
      localStorage.setItem('bmed_auditEvents', JSON.stringify(data));
    }, error => {
      console.error('Firestore auditEvents listener error:', error);
    });
    const unsubMaternity = onSnapshot(collection(db, MATERNITY_COL), snapshot => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as MaternityFile[];
      setMaternityFiles(data);
      localStorage.setItem('bmed_maternityFiles', JSON.stringify(data));
    }, error => {
      console.error('Firestore maternityFiles listener error:', error);
    });
    const unsubWalkIn = onSnapshot(collection(db, WALKIN_COL), snapshot => {
      setWalkInClients(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as WalkInClient));
    });
    const unsubBillingItems = onSnapshot(collection(db, BILLING_ITEMS_COL), snapshot => {
      if (snapshot.empty && !localStorage.getItem('bmed_billing_items_seeded_v1')) {
        localStorage.setItem('bmed_billing_items_seeded_v1', 'true');
        const seedItems: BillingItem[] = [{
          id: 'seed-item-1',
          itemCode: 'CONS-001',
          serviceName: 'General Consultation',
          category: 'Consultation',
          department: 'General',
          unitPrice: 150,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-2',
          itemCode: 'CONS-002',
          serviceName: 'Specialist Consultation',
          category: 'Consultation',
          department: 'Specialist',
          unitPrice: 300,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-3',
          itemCode: 'CONS-003',
          serviceName: 'Review Consultation',
          category: 'Consultation',
          department: 'General',
          unitPrice: 80,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-4',
          itemCode: 'LAB-FBC',
          serviceName: 'Full Blood Count (FBC)',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 120,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-5',
          itemCode: 'LAB-UA',
          serviceName: 'Urinalysis',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 60,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-6',
          itemCode: 'LAB-MRDT',
          serviceName: 'Malaria RDT',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 50,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-7',
          itemCode: 'LAB-LFT',
          serviceName: 'Liver Function Test',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 200,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-8',
          itemCode: 'LAB-HIV',
          serviceName: 'HIV Test',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 90,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-9',
          itemCode: 'LAB-HCG',
          serviceName: 'Beta HCG',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 180,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-10',
          itemCode: 'RAD-PEL',
          serviceName: 'Pelvic Scan',
          category: 'Radiology',
          department: 'Radiology',
          unitPrice: 350,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-11',
          itemCode: 'RAD-OBS',
          serviceName: 'OBS Scan',
          category: 'Radiology',
          department: 'Radiology',
          unitPrice: 400,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-12',
          itemCode: 'RAD-ABD',
          serviceName: 'Abdominal Scan',
          category: 'Radiology',
          department: 'Radiology',
          unitPrice: 380,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-13',
          itemCode: 'RAD-CXR',
          serviceName: 'CXR (Chest X-Ray)',
          category: 'Radiology',
          department: 'Radiology',
          unitPrice: 250,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-14',
          itemCode: 'PHA-DISP',
          serviceName: 'Dispensing Fee',
          category: 'Pharmacy',
          department: 'Pharmacy',
          unitPrice: 30,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-15',
          itemCode: 'PHA-PARA',
          serviceName: 'Paracetamol',
          category: 'Pharmacy',
          department: 'Pharmacy',
          unitPrice: 25,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-16',
          itemCode: 'PHA-AMOX',
          serviceName: 'Amoxicillin',
          category: 'Pharmacy',
          department: 'Pharmacy',
          unitPrice: 70,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-17',
          itemCode: 'PROC-WD',
          serviceName: 'Wound Dressing',
          category: 'Procedures',
          department: 'Nursing',
          unitPrice: 100,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-18',
          itemCode: 'PROC-SUT',
          serviceName: 'Suturing',
          category: 'Procedures',
          department: 'Nursing',
          unitPrice: 220,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-19',
          itemCode: 'THE-MIN',
          serviceName: 'Minor Theatre Procedure',
          category: 'Theatre',
          department: 'Theatre',
          unitPrice: 1200,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-20',
          itemCode: 'MAT-ND',
          serviceName: 'Normal Delivery',
          category: 'Maternity',
          department: 'Maternity',
          unitPrice: 2500,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-21',
          itemCode: 'REP-MR',
          serviceName: 'Medical Report',
          category: 'Medical Reports',
          department: 'Administration',
          unitPrice: 200,
          taxRate: 16,
          discountEligibility: false,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-22',
          itemCode: 'CORP-SERV',
          serviceName: 'Corporate Services',
          category: 'Corporate Services',
          department: 'Administration',
          unitPrice: 500,
          taxRate: 16,
          discountEligibility: false,
          status: 'Active',
          createdAt: new Date().toISOString()
        }];
        seedItems.forEach(item => {
          setDoc(doc(db, BILLING_ITEMS_COL, item.id), item).catch(console.error);
        });
        setBillingItems(seedItems);
      } else {
        setBillingItems(snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }) as BillingItem));
      }
    });
    const unsubInvoices = onSnapshot(collection(db, INVOICES_COL), snapshot => {
      setInvoices(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as Invoice));
    });
    const unsubQuotations = onSnapshot(collection(db, QUOTATIONS_COL), snapshot => {
      setQuotations(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as Quotation));
    });
    const unsubPayments = onSnapshot(collection(db, PAYMENTS_COL), snapshot => {
      setPayments(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as Payment));
    });
    const unsubCreditNotes = onSnapshot(collection(db, CREDIT_NOTES_COL), snapshot => {
      setCreditNotes(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as CreditNote));
    });
    const unsubBillingAudit = onSnapshot(collection(db, BILLING_AUDIT_COL), snapshot => {
      setBillingAuditEvents(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as BillingAuditEvent));
    });
    const unsubMedicines = onSnapshot(collection(db, MEDICINES_COL), snapshot => {
      if (snapshot.empty && !localStorage.getItem('bmed_pharmacy_inventory_seeded_v1')) {
        localStorage.setItem('bmed_pharmacy_inventory_seeded_v1', 'true');
        const now = new Date().toISOString();
        const seedMedicines = normalizeMedicineRecords([{
          id: 'med-paracetamol',
          name: 'Paracetamol',
          sku: 'MED-001',
          category: 'Analgesic',
          genericName: 'Paracetamol',
          strength: '500 mg',
          form: 'Tablet',
          currentQuantity: 240,
          reorderLevel: 80,
          costPrice: 0.8,
          sellingPrice: 2,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }, {
          id: 'med-amoxicillin',
          name: 'Amoxicillin',
          sku: 'MED-002',
          category: 'Antibiotic',
          genericName: 'Amoxicillin',
          strength: '500 mg',
          form: 'Capsule',
          currentQuantity: 45,
          reorderLevel: 60,
          costPrice: 3.5,
          sellingPrice: 7,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }, {
          id: 'med-ibuprofen',
          name: 'Ibuprofen',
          sku: 'MED-003',
          category: 'Anti-inflammatory',
          genericName: 'Ibuprofen',
          strength: '400 mg',
          form: 'Tablet',
          currentQuantity: 0,
          reorderLevel: 40,
          costPrice: 1.4,
          sellingPrice: 3.5,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }, {
          id: 'med-ors',
          name: 'Oral Rehydration Salts',
          sku: 'MED-004',
          category: 'Gastrointestinal',
          genericName: 'ORS',
          strength: 'WHO formula',
          form: 'Sachet',
          currentQuantity: 120,
          reorderLevel: 30,
          costPrice: 2,
          sellingPrice: 5,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }, {
          id: 'med-metronidazole',
          name: 'Metronidazole',
          sku: 'MED-005',
          category: 'Antibiotic',
          genericName: 'Metronidazole',
          strength: '400 mg',
          form: 'Tablet',
          currentQuantity: 36,
          reorderLevel: 50,
          costPrice: 1.2,
          sellingPrice: 3,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }, {
          id: 'med-ferrous',
          name: 'Ferrous Sulphate',
          sku: 'MED-006',
          category: 'Haematinic',
          genericName: 'Ferrous Sulphate',
          strength: '200 mg',
          form: 'Tablet',
          currentQuantity: 150,
          reorderLevel: 50,
          costPrice: 0.7,
          sellingPrice: 2.2,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }]);
        const seedBatches: MedicineBatch[] = [{
          id: 'batch-para-a',
          medicineId: 'med-paracetamol',
          batchNumber: 'PCT-2401',
          quantity: 240,
          expiryDate: '2027-08-31',
          receivedAt: now,
          costPrice: 0.8
        }, {
          id: 'batch-amox-a',
          medicineId: 'med-amoxicillin',
          batchNumber: 'AMX-2407',
          quantity: 45,
          expiryDate: '2026-10-10',
          receivedAt: now,
          costPrice: 3.5
        }, {
          id: 'batch-ors-a',
          medicineId: 'med-ors',
          batchNumber: 'ORS-2410',
          quantity: 120,
          expiryDate: '2027-03-20',
          receivedAt: now,
          costPrice: 2
        }, {
          id: 'batch-metro-a',
          medicineId: 'med-metronidazole',
          batchNumber: 'MET-2403',
          quantity: 36,
          expiryDate: '2026-08-15',
          receivedAt: now,
          costPrice: 1.2
        }, {
          id: 'batch-fer-a',
          medicineId: 'med-ferrous',
          batchNumber: 'FER-2406',
          quantity: 150,
          expiryDate: '2027-01-30',
          receivedAt: now,
          costPrice: 0.7
        }];
        const seedBatch = writeBatch(db);
        seedMedicines.forEach(medicine => seedBatch.set(doc(db, MEDICINES_COL, medicine.id), medicine));
        seedBatches.forEach(batch => seedBatch.set(doc(db, MEDICINE_BATCHES_COL, batch.id), batch));
        seedBatch.commit().catch(error => console.error('Failed to seed pharmacy inventory:', error));
        setMedicines(seedMedicines);
        setMedicineBatches(seedBatches);
      } else {
        const rawMedicines = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }) as MedicineDocument);
        const normalizedMedicines = normalizeMedicineRecords(rawMedicines);
        setMedicines(normalizedMedicines);
        rawMedicines.forEach((rawMedicine, index) => {
          const patch = medicineBackfill(rawMedicine, normalizedMedicines[index]);
          if (Object.keys(patch).length > 0) {
            updateDoc(doc(db, MEDICINES_COL, rawMedicine.id), patch).catch(error => console.error('Failed to normalize medicine master data:', error));
          }
        });
      }
    });
    const unsubMedicineBatches = onSnapshot(collection(db, MEDICINE_BATCHES_COL), snapshot => {
      setMedicineBatches(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as MedicineBatch));
    });
    const unsubSuppliers = onSnapshot(collection(db, SUPPLIERS_COL), snapshot => {
      setSuppliers(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as Supplier));
    });
    const unsubPurchaseOrders = onSnapshot(collection(db, PURCHASE_ORDERS_COL), snapshot => {
      setPurchaseOrders(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as PurchaseOrder));
    });
    const unsubStockMovements = onSnapshot(collection(db, STOCK_MOVEMENTS_COL), snapshot => {
      setStockMovements(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as StockMovement));
    });
    return () => {
      unsubUsers();
      unsubPatients();
      unsubEncounters();
      unsubSessions();
      unsubAudit();
      unsubMaternity();
      unsubWalkIn();
      unsubBillingItems();
      unsubInvoices();
      unsubQuotations();
      unsubPayments();
      unsubCreditNotes();
      unsubBillingAudit();
      unsubMedicines();
      unsubMedicineBatches();
      unsubSuppliers();
      unsubPurchaseOrders();
      unsubStockMovements();
    };
  }, []);
  // Save to localStorage as cache whenever data changes
  useEffect(() => {
    if (!isLoaded) return;
    const data: StorageData = {
      users,
      patients,
      encounters
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [users, patients, encounters, isLoaded]);
  // One-time migration: re-sequence ALL patient file numbers within each
  // registration year so they're contiguous (BMC2025/0001, 0002, 0003 …).
  // Order within a year is by registeredAt ascending (earliest first), with
  // patient.id as a tiebreaker so the result is deterministic across devices.
  // Patient ↔ Encounter ↔ Maternity links use `id`, not `bmcFileNumber`, so
  // renumbering is safe and preserves every relationship.
  //
  // Guarded by a localStorage flag so it runs at most once per browser even
  // across sessions. Bump the flag name (RESEQUENCE_FLAG) to re-trigger.
  const RESEQUENCE_FLAG = 'bmc_resequenced_v1';
  useEffect(() => {
    if (!firestoreReady.current.patients) return;
    if (dedupeRanRef.current) return;
    if (patients.length === 0) return;
    if (localStorage.getItem(RESEQUENCE_FLAG)) {
      dedupeRanRef.current = true;
      return;
    }
    dedupeRanRef.current = true;
    // Group patients by registration year
    const byYear = new Map<number, Patient[]>();
    const currentYear = new Date().getFullYear();
    patients.forEach(p => {
      const year = p.registeredAt ? new Date(p.registeredAt).getFullYear() : currentYear;
      const arr = byYear.get(year) || [];
      arr.push(p);
      byYear.set(year, arr);
    });
    const renumbered: {
      id: string;
      bmcFileNumber: string;
    }[] = [];
    byYear.forEach((group, year) => {
      // Stable ordering: by registeredAt ascending, then by id
      const sorted = [...group].sort((a, b) => {
        const ta = a.registeredAt ? new Date(a.registeredAt).getTime() : 0;
        const tb = b.registeredAt ? new Date(b.registeredAt).getTime() : 0;
        if (ta !== tb) return ta - tb;
        return a.id.localeCompare(b.id);
      });
      const yearPrefix = `BMC${year}/`;
      sorted.forEach((p, idx) => {
        const seq = idx + 1;
        const expected = `${yearPrefix}${seq.toString().padStart(4, '0')}`;
        if (p.bmcFileNumber !== expected) {
          renumbered.push({
            id: p.id,
            bmcFileNumber: expected
          });
        }
      });
    });
    // Mark migration as done regardless of whether anything actually changed,
    // so it doesn't try to run again next session.
    localStorage.setItem(RESEQUENCE_FLAG, new Date().toISOString());
    if (renumbered.length === 0) {
      console.info('[BMC resequence] No changes needed — already in sequence.');
      return;
    }
    console.warn(`[BMC resequence] Updating ${renumbered.length} patient file number(s) to be contiguous per year.`);
    // Apply locally first for instant consistency, then push to Firestore.
    setPatients(prev => prev.map(p => {
      const hit = renumbered.find(r => r.id === p.id);
      return hit ? {
        ...p,
        bmcFileNumber: hit.bmcFileNumber
      } : p;
    }));
    renumbered.forEach(r => {
      updateDoc(doc(db, PATIENTS_COL, r.id), {
        bmcFileNumber: r.bmcFileNumber
      }).catch(err => console.error('Failed to renumber patient', r.id, err));
    });
  }, [patients]);
  const login = (fullName: string, password: string) => {
    const trimmedName = fullName.trim();
    const user = users.find(u => u.fullName.trim().toLowerCase() === trimmedName.toLowerCase());
    if (!user) {
      return {
        ok: false,
        error: 'User not found. Check your full name.'
      };
    }
    if (user.password !== password) {
      return {
        ok: false,
        error: 'Incorrect password.'
      };
    }
    setCurrentUser(user);
    sessionStorage.setItem('hms_session', JSON.stringify(user));
    // Record login session for activity tracking
    const sessionId = `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const {
      device,
      userAgent
    } = parseDevice();
    const session: LoginSession = {
      id: sessionId,
      userId: user.id,
      userName: user.fullName,
      role: user.role,
      loginAt: new Date().toISOString(),
      device,
      userAgent,
      location: 'Resolving…'
    };
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    setLoginSessions(prev => [...prev, session]);
    setDoc(doc(db, SESSIONS_COL, sessionId), session).catch(err => console.error('Failed to record login session:', err));
    // Resolve location asynchronously and patch the session when it arrives
    fetchLocation().then(({
      location,
      ipAddress
    }) => {
      const resolved = location || 'Unknown location';
      setLoginSessions(prev => prev.map(s => s.id === sessionId ? {
        ...s,
        location: resolved,
        ipAddress
      } : s));
      updateDoc(doc(db, SESSIONS_COL, sessionId), {
        location: resolved,
        ipAddress: ipAddress || null
      }).catch(err => console.error('Failed to update session location:', err));
    });
    return {
      ok: true
    };
  };
  const logout = () => {
    const activeSessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (activeSessionId) {
      const logoutAt = new Date().toISOString();
      setLoginSessions(prev => prev.map(s => s.id === activeSessionId ? {
        ...s,
        logoutAt
      } : s));
      updateDoc(doc(db, SESSIONS_COL, activeSessionId), {
        logoutAt
      }).catch(err => console.error('Failed to finalize login session:', err));
      sessionStorage.removeItem(SESSION_ID_KEY);
    }
    setCurrentUser(null);
    sessionStorage.removeItem('hms_session');
  };
  const addUser = (user: Omit<User, 'id'>) => {
    const newUser: User = {
      ...user,
      id: `user-${Date.now()}`
    };
    // Write to Firestore — onSnapshot will update local state
    setDoc(doc(db, USERS_COL, newUser.id), newUser).catch(err => {
      console.error('Failed to add user to Firestore:', err);
      // Fallback: update local state directly
      setUsers(prev => [...prev, newUser]);
    });
  };
  const updateUser = (id: string, data: Partial<User>) => {
    // Optimistic local update
    setUsers(prev => prev.map(u => u.id === id ? {
      ...u,
      ...data
    } : u));
    updateDoc(doc(db, USERS_COL, id), data).catch(err => {
      console.error('Failed to update user in Firestore:', err);
    });
  };
  const deleteUser = (id: string) => {
    setUsers(prev => prev.filter(u => u.id !== id));
    deleteDoc(doc(db, USERS_COL, id)).catch(err => {
      console.error('Failed to delete user from Firestore:', err);
    });
  };
  // Build the next available BMC number for a given year.
  //
  // Strategy:
  //   1. Look at all sequence numbers currently in use for the target year.
  //   2. Walk from 1 upward and pick the FIRST gap (e.g. if 0001, 0002, 0004
  //      are in use, return 0003). This fills holes left by deleted patients
  //      so file numbers stay tight and never collide.
  //   3. If there are no gaps, return max + 1.
  //   4. Final safety: re-check against ALL bmcFileNumbers (any year) and
  //      bump until the candidate is genuinely unique across the database.
  const buildNextBMCNumber = (existingPatients: Patient[], year: number = new Date().getFullYear()): string => {
    const yearPrefix = `BMC${year}/`;
    const usedNumbers = new Set(existingPatients.map(p => p.bmcFileNumber).filter(Boolean));
    const usedSeqsForYear = new Set<number>();
    existingPatients.forEach(p => {
      if (p.bmcFileNumber && p.bmcFileNumber.startsWith(yearPrefix)) {
        const seqStr = p.bmcFileNumber.slice(yearPrefix.length);
        const seq = parseInt(seqStr, 10);
        if (!Number.isNaN(seq)) usedSeqsForYear.add(seq);
      }
    });
    // Find the lowest positive integer not in usedSeqsForYear (gap-filling).
    let nextSeq = 1;
    while (usedSeqsForYear.has(nextSeq)) nextSeq += 1;
    let candidate = `${yearPrefix}${nextSeq.toString().padStart(4, '0')}`;
    // Cross-year safety: ensure global uniqueness against every number on file.
    while (usedNumbers.has(candidate)) {
      nextSeq += 1;
      candidate = `${yearPrefix}${nextSeq.toString().padStart(4, '0')}`;
    }
    return candidate;
  };
  const generateBMCNumber = () => buildNextBMCNumber(patients);
  const addPatient = (patientData: Omit<Patient, 'id'>) => {
    // Compute number against the latest patient list and double-check
    // uniqueness right before save. Bumps if a race created the same number.
    let bmcFileNumber = buildNextBMCNumber(patients);
    const existing = new Set(patients.map(p => p.bmcFileNumber));
    while (existing.has(bmcFileNumber)) {
      // Force the next slot by treating this candidate as already used
      existing.add(bmcFileNumber);
      bmcFileNumber = buildNextBMCNumber([...patients, {
        bmcFileNumber
      } as Patient]);
    }
    const newPatient: Patient = {
      ...patientData,
      id: `pat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      bmcFileNumber,
      registeredAt: new Date().toISOString()
    };
    // Write to Firestore
    setDoc(doc(db, PATIENTS_COL, newPatient.id), newPatient).catch(err => {
      console.error('Failed to add patient to Firestore:', err);
      setPatients(prev => [...prev, newPatient]);
    });
    // Return immediately for the caller (optimistic)
    setPatients(prev => [...prev, newPatient]);
    return newPatient;
  };
  const updatePatient = (id: string, data: Partial<Patient>) => {
    setPatients(prev => prev.map(p => p.id === id ? {
      ...p,
      ...data
    } : p));
    updateDoc(doc(db, PATIENTS_COL, id), data).catch(err => {
      console.error('Failed to update patient in Firestore:', err);
    });
  };
  const deletePatient = (id: string) => {
    setPatients(prev => prev.filter(p => p.id !== id));
    setEncounters(prev => prev.filter(e => e.patientId !== id));
    // Delete from Firestore
    deleteDoc(doc(db, PATIENTS_COL, id)).catch(err => {
      console.error('Failed to delete patient from Firestore:', err);
    });
    // Also delete associated encounters from Firestore
    encounters.filter(e => e.patientId === id).forEach(e => {
      deleteDoc(doc(db, ENCOUNTERS_COL, e.id)).catch(console.error);
    });
  };
  const createEncounter = (patientId: string) => {
    const newEncounter: Encounter = {
      id: `enc-${Date.now()}`,
      patientId,
      startedAt: new Date().toISOString(),
      stage: 'NURSING',
      pendingLab: false,
      pendingRadiology: false,
      labRequests: [],
      radiologyRequests: [],
      prescriptions: []
    };
    // Optimistic local update
    setEncounters(prev => [...prev, newEncounter]);
    // Write to Firestore
    setDoc(doc(db, ENCOUNTERS_COL, newEncounter.id), newEncounter).catch(err => {
      console.error('Failed to create encounter in Firestore:', err);
    });
    return newEncounter;
  };
  const updateEncounter = (id: string, data: Partial<Encounter>) => {
    // Optimistic local update
    setEncounters(prev => prev.map(e => e.id === id ? {
      ...e,
      ...data
    } : e));
    // Write to Firestore
    updateDoc(doc(db, ENCOUNTERS_COL, id), data as Record<string, any>).catch(err => {
      console.error('Failed to update encounter in Firestore:', err);
    });
  };
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const getActiveEncounters = () => {
    return encounters.filter(e => e.stage !== 'COMPLETED');
  };
  // ─── Login session deletion ───
  const deleteLoginSessions = (sessionIds?: string[]) => {
    const ids = sessionIds || loginSessions.map(session => session.id);
    setLoginSessions(prev => prev.filter(session => !ids.includes(session.id)));
    ids.forEach(id => deleteDoc(doc(db, SESSIONS_COL, id)).catch(err => console.error('Failed to delete session:', err)));
  };
  const deleteLoginSessionsBefore = (date: string) => {
    const cutoff = new Date(date).getTime();
    const ids = loginSessions.filter(session => new Date(session.loginAt).getTime() < cutoff).map(session => session.id);
    deleteLoginSessions(ids);
  };
  const deleteLoginSessionsInRange = (start: string, end: string) => {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    const ids = loginSessions.filter(s => {
      const t = new Date(s.loginAt).getTime();
      return t >= startMs && t <= endMs;
    }).map(s => s.id);
    deleteLoginSessions(ids);
  };
  const logAudit = (event: Omit<AuditEvent, 'id' | 'at'>) => {
    const newEvent: AuditEvent = {
      ...event,
      id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      at: new Date().toISOString()
    };
    setAuditEvents(prev => [...prev, newEvent]);
    setDoc(doc(db, AUDIT_COL, newEvent.id), newEvent).catch(err => console.error('Failed to log audit event:', err));
  };
  const addMaternityFile = (file: Omit<MaternityFile, 'id'>) => {
    const newFile: MaternityFile = {
      ...file,
      id: `mat-${Date.now()}`
    };
    setMaternityFiles(prev => [...prev, newFile]);
    setDoc(doc(db, MATERNITY_COL, newFile.id), newFile).catch(err => {
      console.error('Failed to add maternity file to Firestore:', err);
    });
  };
  const updateMaternityFile = (id: string, data: Partial<MaternityFile>) => {
    setMaternityFiles(prev => prev.map(m => m.id === id ? {
      ...m,
      ...data
    } : m));
    updateDoc(doc(db, MATERNITY_COL, id), data as Record<string, any>).catch(err => {
      console.error('Failed to update maternity file in Firestore:', err);
    });
  };
  const deleteMaternityFile = (id: string) => {
    // Optimistic local removal, then delete from Firestore
    setMaternityFiles(prev => prev.filter(m => m.id !== id));
    deleteDoc(doc(db, MATERNITY_COL, id)).catch(err => {
      console.error('Failed to delete maternity file from Firestore:', err);
    });
  };
  const addWalkInClient = (client: Omit<WalkInClient, 'id'>) => {
    const newClient: WalkInClient = {
      ...client,
      id: `walkin-${Date.now()}`
    };
    setWalkInClients(prev => [...prev, newClient]);
    setDoc(doc(db, WALKIN_COL, newClient.id), newClient).catch(console.error);
    return newClient;
  };
  const updateWalkInClient = (id: string, data: Partial<WalkInClient>) => {
    setWalkInClients(prev => prev.map(c => c.id === id ? {
      ...c,
      ...data
    } : c));
    updateDoc(doc(db, WALKIN_COL, id), data as any).catch(console.error);
  };
  const addBillingItem = (item: Omit<BillingItem, 'id'>) => {
    const newItem: BillingItem = {
      ...item,
      id: `item-${Date.now()}`
    };
    setBillingItems(prev => [...prev, newItem]);
    setDoc(doc(db, BILLING_ITEMS_COL, newItem.id), newItem).catch(console.error);
  };
  const updateBillingItem = (id: string, data: Partial<BillingItem>) => {
    setBillingItems(prev => prev.map(i => i.id === id ? {
      ...i,
      ...data
    } : i));
    updateDoc(doc(db, BILLING_ITEMS_COL, id), data as any).catch(console.error);
  };
  const deleteBillingItem = (id: string) => {
    setBillingItems(prev => prev.filter(i => i.id !== id));
    deleteDoc(doc(db, BILLING_ITEMS_COL, id)).catch(console.error);
  };
  const addInvoice = (invoice: Omit<Invoice, 'id'>) => {
    const newInvoice: Invoice = {
      ...invoice,
      id: `inv-${Date.now()}`
    };
    setInvoices(prev => [...prev, newInvoice]);
    setDoc(doc(db, INVOICES_COL, newInvoice.id), newInvoice).catch(console.error);
  };
  const updateInvoice = (id: string, data: Partial<Invoice>) => {
    setInvoices(prev => prev.map(i => i.id === id ? {
      ...i,
      ...data
    } : i));
    updateDoc(doc(db, INVOICES_COL, id), data as any).catch(console.error);
  };
  const addQuotation = (quotation: Omit<Quotation, 'id'>) => {
    const newQuotation: Quotation = {
      ...quotation,
      id: `quo-${Date.now()}`
    };
    setQuotations(prev => [...prev, newQuotation]);
    setDoc(doc(db, QUOTATIONS_COL, newQuotation.id), newQuotation).catch(console.error);
  };
  const updateQuotation = (id: string, data: Partial<Quotation>) => {
    setQuotations(prev => prev.map(q => q.id === id ? {
      ...q,
      ...data
    } : q));
    updateDoc(doc(db, QUOTATIONS_COL, id), data as any).catch(console.error);
  };
  const addPayment = (payment: Omit<Payment, 'id'>) => {
    const newPayment: Payment = {
      ...payment,
      id: `pay-${Date.now()}`
    };
    setPayments(prev => [...prev, newPayment]);
    setDoc(doc(db, PAYMENTS_COL, newPayment.id), newPayment).catch(console.error);
  };
  const addCreditNote = (creditNote: Omit<CreditNote, 'id'>) => {
    const newCreditNote: CreditNote = {
      ...creditNote,
      id: `cn-${Date.now()}`
    };
    setCreditNotes(prev => [...prev, newCreditNote]);
    setDoc(doc(db, CREDIT_NOTES_COL, newCreditNote.id), newCreditNote).catch(console.error);
  };
  const updateCreditNote = (id: string, data: Partial<CreditNote>) => {
    setCreditNotes(prev => prev.map(c => c.id === id ? {
      ...c,
      ...data
    } : c));
    updateDoc(doc(db, CREDIT_NOTES_COL, id), data as any).catch(console.error);
  };
  const addMedicine = (medicineData: MedicineInput) => {
    const now = new Date().toISOString();
    const id = `med-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    let drugCode = buildNextDrugCode(medicines);
    while (reservedDrugCodesRef.current.has(drugCode)) {
      const sequence = medicineCodeSequence(drugCode) + 1;
      drugCode = formatDrugCode(sequence);
    }
    reservedDrugCodesRef.current.add(drugCode);
    const medicine = normalizeMedicineRecord({
      ...medicineData,
      id,
      drugCode,
      medicineId: buildMedicineIdentifier(now, id),
      createdAt: now,
      updatedAt: now,
      // Compatibility aliases are maintained centrally, not entered in the UI.
      name: medicineData.brandName.trim() || medicineData.genericName.trim(),
      sku: drugCode,
      form: medicineData.dosageForm,
      costPrice: medicineData.buyingPrice
    }, drugCode, now);
    setMedicines(previous => [...previous, medicine]);
    setDoc(doc(db, MEDICINES_COL, medicine.id), medicine).catch(error => {
      console.error('Failed to add medicine:', error);
      setMedicines(previous => previous.filter(item => item.id !== medicine.id));
    }).finally(() => reservedDrugCodesRef.current.delete(drugCode));
  };
  const updateMedicine = (id: string, data: Partial<MedicineInput>) => {
    const current = medicines.find(medicine => medicine.id === id);
    if (!current) return;
    const now = new Date().toISOString();
    const nextBrandName = data.brandName?.trim() || current.brandName;
    const nextGenericName = data.genericName?.trim() || current.genericName;
    const nextDosageForm = data.dosageForm || current.dosageForm;
    const nextBuyingPrice = data.buyingPrice ?? current.buyingPrice;
    const normalized = normalizeMedicineRecord({
      ...current,
      ...data,
      id,
      updatedAt: now,
      // Keep the established pharmacy aliases synchronized with master edits.
      name: nextBrandName || nextGenericName,
      sku: current.drugCode,
      form: nextDosageForm,
      costPrice: nextBuyingPrice
    }, current.drugCode, current.createdAt);
    const update = medicineBackfill(current, normalized);
    setMedicines(previous => previous.map(medicine => medicine.id === id ? {
      ...medicine,
      ...normalized
    } : medicine));
    updateDoc(doc(db, MEDICINES_COL, id), update).catch(error => console.error('Failed to update medicine:', error));
  };
  const addSupplier = (supplierData: Omit<Supplier, 'id' | 'createdAt'>) => {
    const supplier: Supplier = {
      ...supplierData,
      id: `supplier-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      createdAt: new Date().toISOString()
    };
    setSuppliers(previous => [...previous, supplier]);
    setDoc(doc(db, SUPPLIERS_COL, supplier.id), supplier).catch(console.error);
  };
  const updateSupplier = (id: string, data: Partial<Supplier>) => {
    setSuppliers(previous => previous.map(supplier => supplier.id === id ? {
      ...supplier,
      ...data
    } : supplier));
    updateDoc(doc(db, SUPPLIERS_COL, id), data).catch(console.error);
  };
  const createPurchaseOrder = (purchaseOrderData: Omit<PurchaseOrder, 'id' | 'orderNumber' | 'createdAt'>) => {
    const order: PurchaseOrder = {
      ...purchaseOrderData,
      id: `po-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      orderNumber: `PO-${String(purchaseOrders.length + 1).padStart(5, '0')}`,
      createdAt: new Date().toISOString()
    };
    setPurchaseOrders(previous => [...previous, order]);
    setDoc(doc(db, PURCHASE_ORDERS_COL, order.id), order).catch(console.error);
  };
  const updatePurchaseOrder = (id: string, data: Partial<PurchaseOrder>) => {
    setPurchaseOrders(previous => previous.map(order => order.id === id ? {
      ...order,
      ...data
    } : order));
    updateDoc(doc(db, PURCHASE_ORDERS_COL, id), data).catch(console.error);
  };
  const recordStockMovement = (movementInput: StockMovementInput) => {
    if (!currentUser) return {
      ok: false,
      error: 'Sign in to record inventory activity.'
    };
    const medicine = medicines.find(item => item.id === movementInput.medicineId);
    if (!medicine) return {
      ok: false,
      error: 'Medicine was not found.'
    };
    if (!Number.isFinite(movementInput.quantityDelta) || movementInput.quantityDelta === 0) {
      return {
        ok: false,
        error: 'Enter a non-zero stock quantity.'
      };
    }
    const after = medicine.currentQuantity + movementInput.quantityDelta;
    if (after < 0) return {
      ok: false,
      error: 'Insufficient stock for this transaction.'
    };
    const now = new Date().toISOString();
    const medicineBatchesWithStock = medicineBatches.filter(batch => batch.medicineId === medicine.id && batch.quantity > 0).sort((a, b) => a.expiryDate.localeCompare(b.expiryDate));
    const usableBatches = medicineBatchesWithStock.filter(batch => !batch.expiryDate || new Date(batch.expiryDate) >= new Date(now));
    const requestedBatches = movementInput.batchId ? medicineBatchesWithStock.filter(batch => batch.id === movementInput.batchId) : usableBatches;
    const selectedInboundBatch = movementInput.batchId ? medicineBatches.find(batch => batch.id === movementInput.batchId) : undefined;
    const createdInboundBatch: MedicineBatch | undefined = movementInput.quantityDelta > 0 && !selectedInboundBatch ? {
      id: `batch-manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      medicineId: medicine.id,
      batchNumber: `${movementInput.type.replace(/\s/g, '').toUpperCase()}-${Date.now().toString().slice(-6)}`,
      quantity: movementInput.quantityDelta,
      expiryDate: '2099-12-31',
      receivedAt: now,
      costPrice: medicine.costPrice
    } : undefined;
    if (movementInput.quantityDelta < 0) {
      const available = requestedBatches.reduce((total, batch) => total + batch.quantity, 0);
      if (available < Math.abs(movementInput.quantityDelta)) {
        return {
          ok: false,
          error: 'No unexpired batch has enough stock for this transaction.'
        };
      }
    }
    const movement: StockMovement = {
      id: `movement-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      medicineId: medicine.id,
      medicineName: medicine.name,
      batchId: movementInput.batchId || createdInboundBatch?.id,
      batchNumber: movementInput.batchId ? medicineBatches.find(batch => batch.id === movementInput.batchId)?.batchNumber : createdInboundBatch?.batchNumber,
      type: movementInput.type,
      quantityDelta: movementInput.quantityDelta,
      quantityBefore: medicine.currentQuantity,
      quantityAfter: after,
      referenceType: movementInput.referenceType || 'Manual',
      referenceId: movementInput.referenceId,
      encounterId: movementInput.encounterId,
      patientId: movementInput.patientId,
      prescriptionId: movementInput.prescriptionId,
      notes: movementInput.notes,
      actorId: currentUser.id,
      actorName: currentUser.fullName,
      createdAt: now
    };
    const batchWrite = writeBatch(db);
    batchWrite.update(doc(db, MEDICINES_COL, medicine.id), {
      currentQuantity: after,
      updatedAt: now
    });
    if (movementInput.quantityDelta < 0) {
      let remaining = Math.abs(movementInput.quantityDelta);
      requestedBatches.forEach(batch => {
        if (remaining <= 0) return;
        const deduction = Math.min(batch.quantity, remaining);
        remaining -= deduction;
        batchWrite.update(doc(db, MEDICINE_BATCHES_COL, batch.id), {
          quantity: batch.quantity - deduction
        });
      });
    } else if (selectedInboundBatch) {
      batchWrite.update(doc(db, MEDICINE_BATCHES_COL, selectedInboundBatch.id), {
        quantity: selectedInboundBatch.quantity + movementInput.quantityDelta
      });
    } else if (createdInboundBatch) {
      batchWrite.set(doc(db, MEDICINE_BATCHES_COL, createdInboundBatch.id), createdInboundBatch);
    }
    batchWrite.set(doc(db, STOCK_MOVEMENTS_COL, movement.id), movement);
    batchWrite.commit().catch(error => console.error('Failed to record stock movement:', error));
    setMedicines(previous => previous.map(item => item.id === medicine.id ? {
      ...item,
      currentQuantity: after,
      updatedAt: now
    } : item));
    if (movementInput.quantityDelta < 0) {
      let remaining = Math.abs(movementInput.quantityDelta);
      setMedicineBatches(previous => previous.map(batch => {
        if (!requestedBatches.some(item => item.id === batch.id) || remaining <= 0) return batch;
        const deduction = Math.min(batch.quantity, remaining);
        remaining -= deduction;
        return {
          ...batch,
          quantity: batch.quantity - deduction
        };
      }));
    } else if (selectedInboundBatch) {
      setMedicineBatches(previous => previous.map(batch => batch.id === selectedInboundBatch.id ? {
        ...batch,
        quantity: batch.quantity + movementInput.quantityDelta
      } : batch));
    } else if (createdInboundBatch) {
      setMedicineBatches(previous => [...previous, createdInboundBatch]);
    }
    setStockMovements(previous => [...previous, movement]);
    return {
      ok: true
    };
  };
  const receiveStock = (receipt: StockReceiptInput) => {
    if (!currentUser) return {
      ok: false,
      error: 'Sign in to receive stock.'
    };
    const medicine = medicines.find(item => item.id === receipt.medicineId);
    if (!medicine) return {
      ok: false,
      error: 'Medicine was not found.'
    };
    if (!Number.isFinite(receipt.quantity) || receipt.quantity <= 0) return {
      ok: false,
      error: 'Enter a valid received quantity.'
    };
    if (!receipt.batchNumber.trim()) return {
      ok: false,
      error: 'Batch number is required.'
    };
    if (!receipt.expiryDate || new Date(receipt.expiryDate) <= new Date()) return {
      ok: false,
      error: 'Expiry date must be in the future.'
    };
    if (!Number.isFinite(receipt.costPrice) || receipt.costPrice < 0) {
      return {
        ok: false,
        error: 'Enter a valid unit cost.'
      };
    }
    const order = receipt.purchaseOrderId ? purchaseOrders.find(item => item.id === receipt.purchaseOrderId) : undefined;
    if (receipt.purchaseOrderId && !order) {
      return {
        ok: false,
        error: 'The selected purchase order was not found.'
      };
    }
    const orderLine = order?.lines.find(line => line.medicineId === receipt.medicineId);
    if (order && (!orderLine || order.status === 'Received' || order.status === 'Cancelled')) {
      return {
        ok: false,
        error: 'This medicine cannot be received against the selected purchase order.'
      };
    }
    if (orderLine && receipt.quantity > orderLine.quantityOrdered - orderLine.quantityReceived) {
      return {
        ok: false,
        error: 'Received quantity exceeds the outstanding purchase order quantity.'
      };
    }
    if (order && receipt.supplierId && receipt.supplierId !== order.supplierId) {
      return {
        ok: false,
        error: 'Select the supplier assigned to this purchase order.'
      };
    }
    const now = new Date().toISOString();
    const after = medicine.currentQuantity + receipt.quantity;
    const matchingBatch = medicineBatches.find(batch => batch.medicineId === medicine.id && batch.batchNumber.trim().toLowerCase() === receipt.batchNumber.trim().toLowerCase());
    const batchRecord: MedicineBatch = matchingBatch ? {
      ...matchingBatch,
      quantity: matchingBatch.quantity + receipt.quantity,
      expiryDate: receipt.expiryDate,
      receivedAt: now,
      costPrice: receipt.costPrice,
      supplierId: receipt.supplierId || order?.supplierId || matchingBatch.supplierId,
      purchaseOrderId: receipt.purchaseOrderId || matchingBatch.purchaseOrderId
    } : {
      id: `batch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      medicineId: medicine.id,
      batchNumber: receipt.batchNumber.trim(),
      quantity: receipt.quantity,
      expiryDate: receipt.expiryDate,
      receivedAt: now,
      costPrice: receipt.costPrice,
      supplierId: receipt.supplierId || order?.supplierId,
      purchaseOrderId: receipt.purchaseOrderId
    };
    const movement: StockMovement = {
      id: `movement-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      medicineId: medicine.id,
      medicineName: medicine.name,
      batchId: batchRecord.id,
      batchNumber: batchRecord.batchNumber,
      type: 'Receipt',
      quantityDelta: receipt.quantity,
      quantityBefore: medicine.currentQuantity,
      quantityAfter: after,
      referenceType: receipt.purchaseOrderId ? 'Purchase Order' : 'Manual',
      referenceId: receipt.purchaseOrderId,
      notes: receipt.notes,
      actorId: currentUser.id,
      actorName: currentUser.fullName,
      createdAt: now
    };
    const batchWrite = writeBatch(db);
    batchWrite.update(doc(db, MEDICINES_COL, medicine.id), {
      currentQuantity: after,
      buyingPrice: receipt.costPrice,
      costPrice: receipt.costPrice,
      updatedAt: now
    });
    batchWrite.set(doc(db, MEDICINE_BATCHES_COL, batchRecord.id), batchRecord);
    batchWrite.set(doc(db, STOCK_MOVEMENTS_COL, movement.id), movement);
    if (order) {
      const lines = order.lines.map(line => line.medicineId === medicine.id ? {
        ...line,
        quantityReceived: Math.min(line.quantityOrdered, line.quantityReceived + receipt.quantity)
      } : line);
      const fullyReceived = lines.every(line => line.quantityReceived >= line.quantityOrdered);
      batchWrite.update(doc(db, PURCHASE_ORDERS_COL, order.id), {
        lines,
        status: fullyReceived ? 'Received' : 'Partially Received'
      });
      setPurchaseOrders(previous => previous.map(item => item.id === order.id ? {
        ...item,
        lines,
        status: fullyReceived ? 'Received' : 'Partially Received'
      } : item));
    }
    batchWrite.commit().catch(error => console.error('Failed to receive stock:', error));
    setMedicines(previous => previous.map(item => item.id === medicine.id ? {
      ...item,
      currentQuantity: after,
      buyingPrice: receipt.costPrice,
      costPrice: receipt.costPrice,
      updatedAt: now
    } : item));
    setMedicineBatches(previous => matchingBatch ? previous.map(batch => batch.id === batchRecord.id ? batchRecord : batch) : [...previous, batchRecord]);
    setStockMovements(previous => [...previous, movement]);
    return {
      ok: true
    };
  };
  const dispenseMedicine = (input: {
    drugName: string;
    encounterId: string;
    patientId: string;
    prescriptionId: string;
  }) => {
    const encounter = encounters.find(item => item.id === input.encounterId);
    const prescription = encounter?.prescriptions.find(item => item.id === input.prescriptionId);
    if (dispensingPrescriptionIdsRef.current.has(input.prescriptionId) || prescription?.status === 'Dispensed' || stockMovements.some(movement => movement.type === 'Dispense' && movement.prescriptionId === input.prescriptionId)) {
      return {
        ok: false,
        error: 'This prescription has already been dispensed.'
      };
    }
    const normalizedDrug = normalizeMedicineName(input.drugName);
    const medicine = medicines.find(item => normalizeMedicineName(item.name) === normalizedDrug || normalizeMedicineName(item.genericName) === normalizedDrug);
    if (!medicine) {
      return {
        ok: false,
        error: 'No matching medicine exists in Pharmacy Inventory. Mark this item Not Available or add stock first.'
      };
    }
    dispensingPrescriptionIdsRef.current.add(input.prescriptionId);
    const result = recordStockMovement({
      medicineId: medicine.id,
      quantityDelta: -1,
      type: 'Dispense',
      referenceType: 'Prescription',
      referenceId: input.prescriptionId,
      encounterId: input.encounterId,
      patientId: input.patientId,
      prescriptionId: input.prescriptionId,
      notes: 'One unit automatically deducted for prescription dispensing.'
    });
    if (!result.ok) dispensingPrescriptionIdsRef.current.delete(input.prescriptionId);
    return result;
  };
  const logBillingAudit = (action: string, description: string) => {
    if (!currentUser) return;
    const newEvent: BillingAuditEvent = {
      id: `baudit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      action,
      description,
      userId: currentUser.id,
      userName: currentUser.fullName,
      createdAt: new Date().toISOString()
    };
    setBillingAuditEvents(prev => [...prev, newEvent]);
    setDoc(doc(db, BILLING_AUDIT_COL, newEvent.id), newEvent).catch(console.error);
  };
  if (!isLoaded) return null;
  return <DataContext.Provider value={{
    currentUser,
    users,
    patients,
    encounters,
    loginSessions,
    auditEvents,
    maternityFiles,
    walkInClients,
    billingItems,
    invoices,
    quotations,
    payments,
    creditNotes,
    billingAuditEvents,
    medicines,
    medicineBatches,
    suppliers,
    purchaseOrders,
    stockMovements,
    addMedicine,
    updateMedicine,
    addSupplier,
    updateSupplier,
    createPurchaseOrder,
    updatePurchaseOrder,
    receiveStock,
    recordStockMovement,
    dispenseMedicine,
    login,
    logout,
    addUser,
    updateUser,
    deleteUser,
    addPatient,
    updatePatient,
    deletePatient,
    createEncounter,
    updateEncounter,
    getPatientEncounters,
    getActiveEncounters,
    deleteLoginSessions,
    deleteLoginSessionsBefore,
    deleteLoginSessionsInRange,
    logAudit,
    addMaternityFile,
    updateMaternityFile,
    deleteMaternityFile,
    addWalkInClient,
    updateWalkInClient,
    addBillingItem,
    updateBillingItem,
    deleteBillingItem,
    addInvoice,
    updateInvoice,
    addQuotation,
    updateQuotation,
    addPayment,
    addCreditNote,
    updateCreditNote,
    logBillingAudit
  }} data-id="element-5087">
      {children}
    </DataContext.Provider>;
};
function normalizeMedicineName(value: string) {
  return value.toLowerCase().replace(/\([^)]*\)/g, '').replace(/\b\d+(?:\.\d+)?\s*(?:mg|mcg|g|ml|iu|%|mg\/5ml)\b/g, '').replace(/[^a-z0-9]/g, '').trim();
}
export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};